/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// v1_842: the review period that sets the next review date at approval.
//
// Until v1_842 approval read report.reviewPeriod, a string the model writes
// (the interview prompt offers "6 months / 1 year / 2 years", the document
// engine's schema fills "1 year") and that a Reprocess keeps as officer
// recorded. The firm's signed section 7 periods reached it only when the
// field was empty, which in practice was never. Now the period comes from
// the firm's signed questionnaire, read at the moment of approval, for the
// case's operating tier; with no signed questionnaire, the platform default
// for the tier applies and the case note says so.

import { tierInForce, tierInForceFromRow } from './tier-in-force.js';
import { TIER_REQUIREMENTS } from './policy-questionnaire.js';

const TIERS = ['Low', 'Medium', 'High'];
// Section 7 accepts any positive number of years up to the platform maximum,
// fractions included (0.5 is six months), so the date is computed in whole
// months: setFullYear(y + 0.5) truncates to y and would set the next review
// date to the approval date itself.
const monthsOf = (years) => Math.max(1, Math.round(Number(years) * 12));
const periodLabel = (months) => months % 12 === 0
  ? (months / 12) + (months === 12 ? ' year' : ' years')
  : months + (months === 1 ? ' month' : ' months');

// The signed periods, numbers only, for get-firm (the browser never receives
// the whole risk_config). Null unless a questionnaire is signed (policyBasis).
export function firmReviewPeriods(riskConfig) {
  const rc = riskConfig && typeof riskConfig === 'object' ? riskConfig : null;
  if (!rc || !rc.policyBasis || typeof rc.policyBasis !== 'object') return null;
  const tr = rc.tierRequirements || {};
  const out = {};
  for (const t of TIERS) {
    const n = Number(tr[t] && tr[t].reviewYears);
    if (Number.isFinite(n) && n > 0) out[t] = n;
  }
  return Object.keys(out).length ? out : null;
}

// The period, its basis and the date, for one approval.
export function reviewPeriodAtApproval({ tier, firmPeriods, policyBasis, approvedAt, firmReadFailed = false }) {
  const t = TIERS.includes(tier) ? tier : null;
  const firmYears = t && firmPeriods && Number.isFinite(Number(firmPeriods[t])) ? Number(firmPeriods[t]) : null;
  const signed = !!(policyBasis && policyBasis.version != null);
  const source = firmYears != null && signed ? 'firm' : (firmReadFailed ? 'default_unread' : 'default');
  const years = source === 'firm' ? firmYears : (t ? TIER_REQUIREMENTS[t].reviewYears : 1);
  const months = monthsOf(years);
  const at = new Date(approvedAt || Date.now());
  const next = new Date(at);
  next.setUTCMonth(next.getUTCMonth() + months);
  return {
    tier: t,
    years,
    months,
    label: periodLabel(months),
    source,
    questionnaireVersion: source === 'firm' ? policyBasis.version : null,
    firmReadFailed: !!firmReadFailed,
    questionnaireSignedAt: source === 'firm' ? (policyBasis.signedAt || null) : null,
    approvedAt: at.toISOString(),
    nextReviewDate: next.toISOString(),
  };
}

// v1_849: the case's current tier, as approval and acceptance read it.
// Model tier pending (29/10/2026): the tier in force (the engine's, or the
// MLRO's adjustment), never the model's riskTier; null while none is.
export function caseTierForReview(report, session) {
  if (report && typeof report === 'object' && Object.keys(report).length) return tierInForce(report);
  return tierInForceFromRow(session || {});
}

// v1_849: what the case's review period panel shows. After approval (or a
// migration's acceptance) the stamp that was written then. Before, what
// approval would set now: the firm's signed period for the case's current
// tier, so a Reprocess that moves the tier moves the period with it; with no
// signed period, the platform default for the tier, said as such. Never the
// string the analysis writes (report.reviewPeriod), which until v1_849 was
// what the panel showed before approval.
export function reviewPeriodPreview({ report, tier, firmPeriods, policyBasis, firmReadFailed = false, approved = false }) {
  const st = report && report.reviewPeriodBasis && typeof report.reviewPeriodBasis === 'object' ? report.reviewPeriodBasis : null;
  if (st && Number(st.months) > 0) {
    const months = Math.round(Number(st.months));
    const at = st.event === 'acceptance' ? 'acceptance' : 'approval';
    const from = st.source === 'firm' ? `the firm's period${st.tier ? ' for ' + st.tier + ' risk' : ''}, policy questionnaire version ${st.questionnaireVersion}` : `the platform default${st.tier ? ' for ' + st.tier + ' risk' : ''}`;
    return { label: periodLabel(months), months, source: 'stamp', tier: st.tier || null, text: `Stamped at ${at}: ${from}.` };
  }
  // Decided before approval stamped a period (before v1_842, or a migration
  // accepted before v1_849): the period the case recorded, said as such. What
  // approval would set now does not apply to a decision already taken.
  if (approved) {
    const p = periodicReviewPeriod(report);
    return { label: p.label, months: p.months, source: p.source, tier: null, text: p.source === 'label'
      ? 'The period the case recorded before approval stamped one. The next periodic review stamps a period from the firm\'s policy.'
      : 'The case has no readable review period, so one year applies. The next periodic review stamps a period from the firm\'s policy.' };
  }
  const p = reviewPeriodAtApproval({ tier, firmPeriods, policyBasis, firmReadFailed });
  const tierText = p.tier ? p.tier + ' risk' : 'this case (no tier on file yet)';
  const text = p.source === 'firm'
    ? `The firm's review period for ${tierText}, policy questionnaire version ${p.questionnaireVersion}. It is stamped at approval, for the tier the case has then.`
    : p.source === 'default_unread'
      ? `The platform default for ${tierText}: the firm's signed review periods could not be read just now. Approval reads them again.`
      : `The platform default for ${tierText}: the firm has no signed review period. It is stamped at approval, for the tier the case has then.`;
  return { label: p.label, months: p.months, source: p.source, tier: p.tier, text };
}

// v1_849: THE REVIEW PERIOD EVERY READER PRINTS. The exported reports (the
// final report and the case file), the second sign off and external review
// pages, and the bank questionnaire print this, never the string the analysis
// writes: the stamp once approval or acceptance wrote one; on a case decided
// before stamps, the period it recorded; before a decision, what the panel
// shows (the firm's period for the case's current tier) where the caller has
// the firm's periods, and otherwise that the period is set at approval.
export const REVIEW_PERIOD_AT_APPROVAL = "Set at approval, from the firm's review period for the case's tier";
export function reviewPeriodShown(report, ctx = {}) {
  const c = ctx || {};
  const st = report && report.reviewPeriodBasis && typeof report.reviewPeriodBasis === 'object' ? report.reviewPeriodBasis : null;
  if (st && Number(st.months) > 0) return periodLabel(Math.round(Number(st.months)));
  if (c.approved) return periodicReviewPeriod(report).label;
  if ('firmPeriods' in c || c.firmReadFailed) {
    return reviewPeriodPreview({ report, tier: caseTierForReview(report, c.session), firmPeriods: c.firmPeriods || null, policyBasis: c.policyBasis || null, firmReadFailed: !!c.firmReadFailed }).label;
  }
  return REVIEW_PERIOD_AT_APPROVAL;
}
// v1_849: a rebuild (Reprocess, interview submit, officer regenerate) keeps the
// review period only once a decision set it: a stamp on the report, or a case
// approved before stamps (its recorded label is what its periodic review reads).
export function shouldCarryReviewPeriod(priorReport, session) {
  const r = priorReport || {};
  if (r.reviewPeriodBasis && typeof r.reviewPeriodBasis === 'object') return true;
  const s = session || {};
  return !!(s.approved_at || s.mlro_decision === 'Approve');
}

// The context from the dashboard's firm (get-firm gives the periods, numbers
// only, and the signed basis); no firm loaded is a failed read, said as such.
export const reviewPeriodCtxFromFirm = (firm) => ({ firmPeriods: (firm && firm.reviewPeriods) || null, policyBasis: (firm && firm.policyBasis) || null, firmReadFailed: !firm });
// A session whose report carries the period shown, for a caller handing the
// case to an exporter or another page.
export function withShownReviewPeriod(session, ctx = {}) {
  const s = session || {};
  const r = s.report || {};
  const approved = !!(s.approved_at || s.mlro_decision === 'Approve');
  return { ...s, report: { ...r, reviewPeriod: reviewPeriodShown(r, { ...ctx, session: s, approved }), reviewPeriodShown: true } };
}

// v1_849: the stamp Accept for Ongoing Monitoring writes on a migrated client.
// The period and its basis are what approval would stamp for the case's tier
// now (so the periodic review that follows runs on the firm's period); the
// next review date is the one the officer set in the acceptance dialog, which
// may follow the previous system's cycle, and the stamp says whether it
// matches the period.
export function reviewPeriodAtAcceptance({ tier, firmPeriods, policyBasis, acceptedAt, reviewDate, firmReadFailed = false }) {
  const p = reviewPeriodAtApproval({ tier, firmPeriods, policyBasis, approvedAt: acceptedAt, firmReadFailed });
  const periodDate = p.nextReviewDate;
  const set = reviewDate ? new Date(reviewDate).toISOString() : periodDate;
  return { ...p, event: 'acceptance', acceptedAt: p.approvedAt, periodReviewDate: periodDate, nextReviewDate: set, officerSetDate: set.slice(0, 10) !== periodDate.slice(0, 10) };
}

// v1_849: the case note written at acceptance.
export function reviewPeriodAcceptanceNote(stamp) {
  const fmt = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const tierText = stamp.tier ? stamp.tier + ' risk' : 'this case (no tier on file)';
  const basis = stamp.source === 'firm'
    ? `the firm's review period for ${tierText}, ${stamp.label}, from policy questionnaire version ${stamp.questionnaireVersion}`
    : stamp.source === 'default_unread'
      ? `the platform default for ${tierText}, ${stamp.label}, because the firm's signed review periods could not be read at acceptance; check the firm's questionnaire`
      : `the platform default for ${tierText}, ${stamp.label}, because the firm has no signed review period`;
  const date = stamp.officerSetDate
    ? ` The next review date, ${fmt(stamp.nextReviewDate)}, is the one set at acceptance; the period alone would give ${fmt(stamp.periodReviewDate)}.`
    : ` The next review date is ${fmt(stamp.nextReviewDate)}.`;
  return `Review period stamped at acceptance: ${basis}. Periodic reviews on this client run on this period.${date}`;
}

// The case note written at approval.
export function reviewPeriodNote(stamp, { previous } = {}) {
  const when = new Date(stamp.nextReviewDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const tierText = stamp.tier ? stamp.tier + ' risk' : 'this case (no tier on file)';
  const basis = stamp.source === 'firm'
    ? `the firm's review period for ${tierText} is ${stamp.label}, from policy questionnaire version ${stamp.questionnaireVersion}${stamp.questionnaireSignedAt ? ' signed ' + new Date(stamp.questionnaireSignedAt).toLocaleDateString('en-GB') : ''}`
    : stamp.source === 'default_unread'
      ? `the firm's signed review periods could not be read at approval, so the platform default for ${tierText}, ${stamp.label}, applies; check the firm's questionnaire and correct the date if it differs`
      : `the firm has no signed review period, so the platform default for ${tierText}, ${stamp.label}, applies`;
  const prior = previous && String(previous).trim() && String(previous).trim().toLowerCase() !== stamp.label
    ? ` The period the analysis had recorded (${String(previous).trim()}) is not used.`
    : '';
  return `Next review date set to ${when} at approval: ${basis}.${prior}`;
}

// v1_846: the period a completed periodic review sets the next date by. The
// stamp approval writes (report.reviewPeriodBasis, v1_842) governs, in whole
// months; only a case approved before v1_842 falls back to the label, read in
// months or years; an unreadable label gives one year and says so.
export function periodicReviewPeriod(report, { now } = {}) {
  const r = report && typeof report === 'object' ? report : {};
  const basis = r.reviewPeriodBasis && typeof r.reviewPeriodBasis === 'object' ? r.reviewPeriodBasis : null;
  let months = null, source = 'unreadable';
  if (basis && Number.isFinite(Number(basis.months)) && Number(basis.months) > 0) { months = Math.round(Number(basis.months)); source = 'stamp'; }
  else {
    const label = String(r.reviewPeriod || '').toLowerCase();
    const m = label.match(/(\d+(?:\.\d+)?)\s*months?\b/), y = label.match(/(\d+(?:\.\d+)?)\s*years?\b/);
    if (m) { months = Math.max(1, Math.round(Number(m[1]))); source = 'label'; }
    else if (y) { months = Math.max(1, Math.round(Number(y[1]) * 12)); source = 'label'; }
  }
  if (months == null) months = 12;
  const at = new Date(now || Date.now());
  const next = new Date(at);
  next.setUTCMonth(next.getUTCMonth() + months);
  return { months, label: periodLabel(months), source, nextReviewDate: next.toISOString() };
}

export function periodicReviewBasisText(p, report) {
  if (p.source === 'stamp') return `${p.label}, the review period stamped at approval`;
  if (p.source === 'label') return `${p.label}, the review period recorded on the case before approval stamped one`;
  const raw = report && report.reviewPeriod ? ` (the case records "${String(report.reviewPeriod).trim()}")` : '';
  return `1 year: the case has no readable review period${raw}, so one year applies; correct the date if the firm's period differs`;
}
